import React from 'react';

import { Column } from 'primereact/column';
import type { Task } from 'types/interfaces';
import { getTaskStatusLabel } from 'utils/taskExecutorPickerHelpers';

import { ExecutorPickerCell } from './cells/ExecutorPickerCell';
import { ManualAssignCell } from './cells/ManualAssignCell';
import { employeeDistributionColumnWidths as W } from './columnWidths';
import { formatTaskDateCell } from './lib/distributionHelpers';

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const idBody = (row: Task) => {
  const idText = String(row.id ?? '');

  return (
    <div className="flex align-items-center gap-2">
      <a
        href={`/${row.id}`}
        target="_blank"
        rel="noreferrer"
        style={{ userSelect: 'text' }}
      >
        {row.id}
      </a>
      <i
        className="pi pi-copy cursor-pointer text-500"
        title="Скопировать номер"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!idText) return;
          copyToClipboard(idText).catch((error) => {
            console.error('Failed to copy task id', error);
          });
        }}
      />
    </div>
  );
};

export interface DistributionColumnsOptions {
  tableControlsEnabled?: boolean;
}

const controlProps = (enabled: boolean) =>
  enabled
    ? {
        sortable: true,
        filter: true
      }
    : {};

export const getDistributionColumns = ({
  tableControlsEnabled = true
}: DistributionColumnsOptions = {}): React.ReactNode[] => [
  <Column
    key="selection"
    selectionMode="multiple"
    style={{ width: W.selection }}
    headerStyle={{ width: W.selection }}
  />,
  <Column
    key="id"
    field="id"
    header="№"
    {...controlProps(tableControlsEnabled)}
    filterField="id"
    filterPlaceholder="Поиск по номеру"
    showAddButton={false}
    showFilterMatchModes={false}
    body={idBody}
    style={{ width: W.id }}
  />,
  <Column
    key="taskType"
    field="taskType"
    header="Тип"
    {...controlProps(tableControlsEnabled)}
    filterField="taskType"
    filterPlaceholder="Поиск по типу"
    showAddButton={false}
    showFilterMatchModes={false}
    style={{ width: W.taskType }}
  />,
  <Column
    key="requiredEmployees"
    field="requiredEmployees"
    header="Треб."
    showAddButton={false}
    showFilterMatchModes={false}
    style={{ width: W.requiredEmployees }}
  />,
  <Column
    key="name"
    field="name"
    header="Название"
    {...controlProps(tableControlsEnabled)}
    filterField="name"
    filterPlaceholder="Поиск по названию"
    showAddButton={false}
    showFilterMatchModes={false}
    style={{ width: W.name }}
  />,
  <Column
    key="departmentName"
    field="departmentName"
    header="Участок"
    {...controlProps(tableControlsEnabled)}
    filterField="departmentName"
    filterPlaceholder="Поиск по участку"
    showAddButton={false}
    showFilterMatchModes={false}
    style={{ width: W.departmentName }}
  />,
  <Column
    key="creationDate"
    field="creationDate"
    header="Регистрация"
    {...controlProps(tableControlsEnabled)}
    filterField="creationDate"
    filterPlaceholder="Поиск по дате"
    showAddButton={false}
    showFilterMatchModes={false}
    body={(r: Task) =>
      formatTaskDateCell(
        (r as any).creationDate || (r as any).creationTime
      )
    }
    style={{ width: W.creationDate }}
  />,
  <Column
    key="taskStatus"
    field="taskStatus"
    header="Статус"
    {...controlProps(tableControlsEnabled)}
    filterField="taskStatus"
    filterPlaceholder="Поиск по статусу"
    showAddButton={false}
    showFilterMatchModes={false}
    body={(r: Task) => getTaskStatusLabel(r.taskStatus)}
    style={{ width: W.taskStatus }}
  />,
  <Column
    key="plannedStartDate"
    field="plannedStartDate"
    header="Плановое начало"
    {...controlProps(tableControlsEnabled)}
    filterField="plannedStartDate"
    filterPlaceholder="дд.мм.гггг"
    showAddButton={false}
    showFilterMatchModes={false}
    body={(r: Task) =>
      formatTaskDateCell(
        (r as any).plannedStartDate || (r as any).planStart
      )
    }
    style={{ width: W.plannedStartDate }}
  />,
  <Column
    key="plannedEndDate"
    field="plannedEndDate"
    header="Плановое завершение"
    {...controlProps(tableControlsEnabled)}
    filterField="plannedEndDate"
    filterPlaceholder="дд.мм.гггг"
    showAddButton={false}
    showFilterMatchModes={false}
    body={(r: Task) =>
      formatTaskDateCell(
        (r as any).plannedEndDate || (r as any).planFinish
      )
    }
    style={{ width: W.plannedEndDate }}
  />,
  <Column
    key="employees"
    header="Сотрудники"
    body={(r: Task) => <ExecutorPickerCell task={r} />}
    style={{ width: W.employees }}
  />,
  <Column
    key="actions"
    header=""
    body={(r: Task) => <ManualAssignCell task={r} />}
    style={{ width: W.manualAssign }}
  />
];
